import * as _ from 'lodash';
import produce from 'immer';
import * as ID from '../../util/id';
import * as HL from './cg/hl';
import * as CG from './cg/graph';
import * as Op from './cg/ops';
import {ResolverContext} from './cg/resolverContext';
import * as Types from './types';
import * as MediaTable from './mediaTable';
import * as TypeHelpers from './typeHelpers';

type ColumnId = string;

interface TableColumnPanel {
  panelId: string;
  panelConfig: any;
}

interface Sort {
  dir: 'asc' | 'desc';
  columnId: string;
}

type ColumnSelectFunctions = {
  [id: string]: CG.NodeOrVoidNode;
};

type ColumnFilterFunctions = {
  [id: string]: CG.NodeOrVoidNode;
};

type ColumnOrder = ColumnId[];
type GroupByColumns = ColumnId[];

export interface TableState {
  columns: {
    [id: string]: TableColumnPanel;
  };

  preFilterFunction: CG.NodeOrVoidNode;

  // A table's rows node depends on the filters,
  // which depend on the column filter and select functions.
  // Store them seperately from the table column panel config,
  // so we don't recompute the row node if the panel config
  // changes
  columnNames: {[id: string]: string};
  columnSelectFunctions: ColumnSelectFunctions;

  // Column filters are applied after grouping. They are currently
  // disabled in the UI.
  columnFilterFunctions: ColumnFilterFunctions;
  order: ColumnOrder;
  groupBy: GroupByColumns;
  sort: Sort[];
  pageSize: number;
  page: number;
}

function newColumnId() {
  return 'col-' + ID.ID();
}

export function emptyTable(inputArrayNode: CG.Node): TableState {
  return {
    columns: {},
    columnNames: {},
    preFilterFunction: CG.voidNode(),
    columnSelectFunctions: {},
    columnFilterFunctions: {},
    order: [],
    groupBy: [],
    sort: [],
    pageSize: 10,
    page: 0,
  };
}

export async function initTableWithPickColumns(
  context: ResolverContext,
  pickCols: string[],
  inputArrayNode: CG.Node
) {
  let ts = emptyTable(inputArrayNode);
  const exNode = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,
    inputArrayNode
  );
  for (const colKey of pickCols) {
    ts = appendColumn(ts, inputArrayNode);
    const selFn = Op.opPick({
      obj: CG.varNode(exNode.type, 'x') as any,
      key: Op.constString(colKey),
    });

    const newFn = await HL.updateFunctionForInputs(context, selFn, {x: exNode});

    ts = updateColumnSelect(ts, ts.order[ts.order.length - 1], newFn);
  }
  return ts;
}

// Try to pick nice default columns to make a table for the given object
// type. See the initial columns test in tableState.test.ts to see examples
// of current behavior.
export function initTableColumnsFromObjectType(
  objectType: Types.ObjectType
): string[] {
  const allPaths = TypeHelpers.allObjPaths(objectType);
  const sameLengthAndTypeGroups = Object.values(
    _.groupBy(allPaths, path => path.path.length)
  );

  const merged: TypeHelpers.PathType[] = sameLengthAndTypeGroups.flatMap(
    paths => {
      const pathLength = paths[0].path.length;

      // Group all paths by their final component.
      const groupedByTail = _.groupBy(paths, path => path.path[pathLength - 1]);

      return Object.values(groupedByTail).flatMap(sameTailPaths => {
        // Then group by type
        const groupedByType = _.groupBy(sameTailPaths, tp =>
          // Don't group media types, force them into separate groups
          // by returning a random group key.
          Types.HANDLED_MEDIA_TYPES.some(mediaType =>
            Types.isAssignableTo(mediaType, tp.type)
          )
            ? ID.ID()
            : Types.toString(Types.nonNullable(tp.type))
        );
        return Object.values(groupedByType).flatMap(sameTypeAndTailPaths => {
          // Only one in this group, return unmodified path.
          if (sameTypeAndTailPaths.length === 1) {
            return [sameTypeAndTailPaths[0]];
          }
          // More than one and all the same type, return '*.*.<tail_component>'
          const starredPathType: TypeHelpers.PathType = {
            type: sameTypeAndTailPaths[0].type,
            path: _.range(0, pathLength - 1)
              .map(i => {
                // Only add a "*" in the case of variability
                if (
                  sameTypeAndTailPaths.reduce(
                    (prevBool, currPath) =>
                      prevBool &&
                      currPath.path[i] === sameTypeAndTailPaths[0].path[i],
                    true
                  )
                ) {
                  return sameTypeAndTailPaths[0].path[i];
                }
                return '*';
              })
              .concat([sameTypeAndTailPaths[0].path[pathLength - 1]]),
          };
          return starredPathType;
        });
      });
    }
  );

  // Sort order: id columns, media columns, string columns, other
  const sorted = _.sortBy(merged, pt => {
    if (MediaTable.isIdColumnName(pt.path[pt.path.length - 1])) {
      return -10;
    } else if (
      Types.HANDLED_MEDIA_TYPES.some(mediaType =>
        Types.isAssignableTo(mediaType, pt.type)
      )
    ) {
      return 0;
    } else if (Types.isAssignableTo('string', pt.type)) {
      return 5;
    }
    return 10;
  });
  return sorted.map(path => path.path.join('.'));
}

// Given a row node that we're going to render as a table, try to
// pick a good set of default columns.
// This doesn't really have enough information to do a great job.
// We may want to call it from WBJoinedTable and WBTable instead
// of PanelTable, where we no longer have a table and just have an
// array of objects. Or we could walk up the graph from here to
// determine where our array came from...
export function initTableWithAllColumnsFromTableType(
  context: ResolverContext,
  inputArrayNode: CG.Node
) {
  const arrayType = inputArrayNode.type;
  if (Types.isSimpleType(arrayType) || arrayType.type !== 'array') {
    throw new Error('invalid');
  }
  let columns: string[] = [];
  const objectType = arrayType.objectType;
  if (!Types.isSimpleType(objectType) && objectType.type === 'object') {
    columns = initTableColumnsFromObjectType(objectType);
  }
  return initTableWithPickColumns(context, columns, inputArrayNode);
}

export function getExampleRow(exampleRowsNode: CG.Node) {
  return Op.opIndex({
    arr: exampleRowsNode as any,
    index: CG.varNode('number', 'n'),
  });
}

export function getRowExampleNode(
  preFilterFunction: CG.NodeOrVoidNode,
  groupByColumns: GroupByColumns,
  columnSelectFunctions: ColumnSelectFunctions,
  columnFilterFunctions: ColumnFilterFunctions,
  sortByColumns: Sort[],
  inputArrayNode: CG.Node
) {
  const visibleRowsNode = getRowsNode(
    preFilterFunction,
    groupByColumns,
    columnSelectFunctions,
    columnFilterFunctions,
    sortByColumns,
    inputArrayNode
  );
  return getExampleRow(visibleRowsNode);
}

export function getPreFilterExampleNode(inputArrNode: CG.Node) {
  return Op.opIndex({
    arr: inputArrNode as any,
    index: CG.varNode('number', 'n'),
  });
}

export function getPreFilterFrame(inputArrNode: CG.Node) {
  return {x: getPreFilterExampleNode(inputArrNode)};
}

export function getExampleFrameFromExampleRow(
  ts: TableState,
  colId: string,
  exampleRow: CG.Node
) {
  return {x: exampleRow};
}

export function getSelectedFrameFromExampleRow(
  ts: TableState,
  colId: string,
  exampleRow: CG.Node
) {
  const frame = getExampleFrameFromExampleRow(ts, colId, exampleRow);
  const selFn = ts.columnSelectFunctions[colId];
  if (selFn.nodeType === 'void') {
    return {v: CG.voidNode()};
  }
  return {v: HL.callFunction(selFn, {x: frame.x})};
}

export function appendColumn(ts: TableState, inputNode: CG.Node) {
  const colId = newColumnId();
  const rowExampleNode = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,
    inputNode
  );
  return produce(ts, draft => {
    draft.columns[colId] = {
      panelId: '',
      panelConfig: {},
    };
    draft.columnNames[colId] = '';
    draft.columnSelectFunctions[colId] = CG.varNode(rowExampleNode.type, 'x');
    draft.columnFilterFunctions[colId] = CG.varNode(rowExampleNode.type, 'v');
    draft.order.push(colId);
  });
}

export function updatePreFilter(ts: TableState, filterFn: CG.NodeOrVoidNode) {
  return produce(ts, draft => {
    draft.preFilterFunction = filterFn;
  });
}

export function updateColumnName(ts: TableState, colId: string, name: string) {
  return produce(ts, draft => {
    draft.columnNames[colId] = name;
  });
}

export function updateColumnSelect(
  ts: TableState,
  colId: string,
  selectFn: CG.NodeOrVoidNode
) {
  return produce(ts, draft => {
    const newType = selectFn.type;
    const oldType = draft.columnSelectFunctions[colId].type;
    draft.columnSelectFunctions[colId] = selectFn;
    // Reset filter function if the type changed
    if (!_.isEqual(oldType, newType)) {
      draft.columnFilterFunctions[colId] = CG.varNode(selectFn.type, 'v');
    }
  });
}

export function updateColumnFilter(
  ts: TableState,
  colId: string,
  filterFn: CG.NodeOrVoidNode
) {
  return produce(ts, draft => {
    draft.columnFilterFunctions[colId] = filterFn;
  });
}

export function updateColumnPanelId(
  ts: TableState,
  colId: string,
  panelId: string
) {
  return produce(ts, draft => {
    draft.columns[colId].panelId = panelId;
  });
}

export function updateColumnPanelConfig(
  ts: TableState,
  colId: string,
  config: any
) {
  return produce(ts, draft => {
    Object.assign(draft.columns[colId].panelConfig, config);
  });
}

export function nextPage(ts: TableState) {
  return produce(ts, draft => {
    draft.page += 1;
  });
}

export function prevPage(ts: TableState) {
  return produce(ts, draft => {
    draft.page -= 1;
  });
}

export async function enableGroupByCol(
  context: ResolverContext,
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node
) {
  if (ts.columns[colId] == null) {
    throw new Error('invalid group by col id' + colId);
  }
  if (ts.groupBy.includes(colId)) {
    return ts;
  }
  ts = produce(ts, draft => {
    draft.groupBy.push(colId);
  });
  const exampleRow = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,
    inputArrayNode
  );
  ts = await refreshSelectFunctions(ts, exampleRow, context);
  return ts;
}

export async function disableGroupByCol(
  context: ResolverContext,
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node
) {
  if (ts.columns[colId] == null) {
    throw new Error('invalid group by col id' + colId);
  }
  if (!ts.groupBy.includes(colId)) {
    return ts;
  }
  ts = produce(ts, draft => {
    const orderIndex = ts.groupBy.indexOf(colId);
    draft.groupBy.splice(orderIndex, 1);
  });
  const exampleRow = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,
    inputArrayNode
  );
  ts = await refreshSelectFunctions(ts, exampleRow, context);
  return ts;
}

export async function enableSortByCol(
  context: ResolverContext,
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node,
  asc: boolean
) {
  const dir = asc ? 'asc' : 'desc';
  if (ts.columns[colId] == null) {
    throw new Error('invalid sort by col id' + colId);
  }
  let matchedIndex: number | null = null;
  for (let i = 0; i < ts.sort.length; i++) {
    if (ts.sort[i].columnId === colId) {
      matchedIndex = i;
      if (ts.sort[i].dir === dir) {
        return ts;
      } else {
        break;
      }
    }
  }
  ts = produce(ts, draft => {
    const newSort: Sort = {
      dir,
      columnId: colId,
    };
    if (matchedIndex == null) {
      draft.sort.push(newSort);
    } else {
      draft.sort[matchedIndex] = newSort;
    }
  });
  return ts;
}

export async function disableSortByCol(
  context: ResolverContext,
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node
) {
  ts = produce(ts, draft => {
    draft.sort = draft.sort.filter(s => s.columnId !== colId);
  });
  return ts;
}

export function insertColumnRight(
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node
) {
  const colIndex = ts.order.indexOf(colId);
  if (colIndex === -1) {
    throw new Error('invalid insert col id' + colId);
  }

  const newColId = newColumnId();
  const rowExampleNode = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,

    inputArrayNode
  );
  return produce(ts, draft => {
    draft.columns[newColId] = {
      panelId: '',
      panelConfig: {},
    };
    draft.columnNames[newColId] = '';
    draft.columnSelectFunctions[newColId] = CG.varNode(
      rowExampleNode.type,
      'x'
    );
    draft.columnFilterFunctions[newColId] = CG.varNode(
      rowExampleNode.type,
      'v'
    );
    draft.order.splice(colIndex + 1, 0, newColId);
  });
}

export function insertColumnLeft(
  ts: TableState,
  colId: string,
  inputArrayNode: CG.Node
) {
  const colIndex = ts.order.indexOf(colId);
  if (colIndex === -1) {
    throw new Error('invalid insert col id' + colId);
  }

  const newColId = newColumnId();
  const rowExampleNode = getRowExampleNode(
    ts.preFilterFunction,
    ts.groupBy,
    ts.columnSelectFunctions,
    ts.columnFilterFunctions,
    ts.sort,

    inputArrayNode
  );
  return produce(ts, draft => {
    draft.columns[newColId] = {
      panelId: '',
      panelConfig: {},
    };
    draft.columnNames[newColId] = '';
    draft.columnSelectFunctions[newColId] = CG.varNode(
      rowExampleNode.type,
      'x'
    );
    draft.columnFilterFunctions[newColId] = CG.varNode(
      rowExampleNode.type,
      'v'
    );
    draft.order.splice(colIndex, 0, newColId);
  });
}

export function removeColumn(ts: TableState, colId: string) {
  const colIndex = ts.order.indexOf(colId);
  if (colIndex === -1) {
    throw new Error('invalid insert col id' + colId);
  }
  return produce(ts, draft => {
    delete draft.columns[colId];
    delete draft.columnNames[colId];
    delete draft.columnSelectFunctions[colId];
    delete draft.columnFilterFunctions[colId];
    draft.sort = draft.sort.filter(sort => sort.columnId !== colId);
    draft.order.splice(colIndex, 1);
  });
}

async function refreshSelectFunctions(
  ts: TableState,
  exampleNode: CG.Node,
  context: ResolverContext
) {
  const ungroupedCols = getColumnRenderOrder(ts);
  const newSelectFns = await Promise.all(
    ungroupedCols.map(colId =>
      HL.updateFunctionForInputs(
        context,
        ts.columnSelectFunctions[colId] as any,
        {
          x: exampleNode,
        }
      )
    )
  );
  ungroupedCols.forEach(
    (colId, i) => (ts = updateColumnSelect(ts, colId, newSelectFns[i]))
  );
  return ts;
}

export function getColumnRenderOrder(tableState: TableState) {
  return tableState.order.filter(colId => !tableState.groupBy.includes(colId));
}

// Computes a single predicate function from filters set on all columns
function getFilterFn(
  groupByColumns: GroupByColumns,
  columnSelectFunctions: ColumnSelectFunctions,
  columnFilterFunctions: ColumnFilterFunctions,
  inputNode: CG.Node
) {
  const exampleRowNode = Op.opIndex({
    arr: inputNode as any,
    index: CG.varNode('number', 'n'),
  });

  const predFns: CG.Node[] = [];
  for (const colId of Object.keys(columnSelectFunctions)) {
    const colSelectFunction = columnSelectFunctions[colId];
    const colFilterFunction = columnFilterFunctions[colId];
    if (colFilterFunction.nodeType !== 'var') {
      if (groupByColumns.includes(colId)) {
        // For columns we've grouped by, the filter applies directly to the group
        // key.
        let predicateFn: CG.Node = Op.opGetTag({
          obj: CG.varNode(exampleRowNode.type, 'x'),
        });
        if (colFilterFunction.nodeType !== 'void') {
          predicateFn = HL.callFunction(colFilterFunction, {v: predicateFn});
          predFns.push(predicateFn);
        }
      } else {
        let predicateFn: CG.Node = CG.varNode(exampleRowNode.type, 'x');
        if (colSelectFunction.nodeType !== 'void') {
          predicateFn = HL.callFunction(colSelectFunction, {
            x: predicateFn as any,
          });
        }
        if (colFilterFunction.nodeType !== 'void') {
          predicateFn = HL.callFunction(colFilterFunction, {v: predicateFn});
        }
        predFns.push(predicateFn);
      }
    }
  }
  if (predFns.length === 0) {
    // No filters set, just pass argument variable through (identity)
    return CG.varNode('any', 'x');
  }
  const andArg: {[key: string]: CG.Node} = {};
  for (let i = 0; i < predFns.length; i++) {
    andArg[i] = predFns[i];
  }
  return Op.opAnd(andArg as any);
}

function getComparableDataFn(
  columnSelectFunctions: ColumnSelectFunctions,
  sortBy: Sort[],
  inputNode: CG.Node
) {
  if (sortBy.length === 0) {
    // No sort set, just pass argument variable through (identity)
    return CG.varNode('any', 'x');
  }

  const exampleRowNode = Op.opIndex({
    arr: inputNode as any,
    index: CG.varNode('number', 'n'),
  });

  const colVals: {[key: string]: CG.Node} = {};
  for (const sort of sortBy) {
    const colSelectFunction = columnSelectFunctions[sort.columnId];
    let colVal: CG.Node = CG.varNode(exampleRowNode.type, 'x');
    if (colSelectFunction.nodeType !== 'void') {
      colVal = HL.callFunction(colSelectFunction, {
        x: colVal as any,
      });
    }
    colVals[sort.columnId] = colVal;
  }
  if (Object.keys(colVals).length === 0) {
    return CG.varNode('any', 'x');
  }
  return Op.opArrayUnsafe(colVals as any);
}

export function getPagedRowsNode(
  pageSize: number,
  page: number,
  rowsNode: CG.Node
) {
  let node = Op.opOffset({
    arr: rowsNode as any,
    offset: Op.constNumber(pageSize * page),
  });
  node = Op.opLimit({
    arr: node as any,
    limit: Op.constNumber(pageSize),
  });
  return node;
}

export function getRowsNode(
  preFilterFunction: CG.NodeOrVoidNode,
  groupByColumns: GroupByColumns,
  columnSelectFunctions: ColumnSelectFunctions,
  columnFilterFunctions: ColumnFilterFunctions,
  sortByColumns: Sort[],
  inputNode: CG.Node
) {
  let node = inputNode;

  // pre-filter
  if (preFilterFunction.nodeType === 'output') {
    node = Op.opFilter({
      arr: node as any,
      filterFn: Op.constFunction(preFilterFunction) as any,
    });
  }

  // group
  if (groupByColumns.length > 0) {
    const groupByFn = columnSelectFunctions[groupByColumns[0]];
    node = Op.opGroupBy({
      arr: node as any,
      groupByFn: Op.constNodeUnsafe(groupByFn.type, groupByFn) as any,
    });
  }
  // filter
  const filterFn = getFilterFn(
    groupByColumns,
    columnSelectFunctions,
    columnFilterFunctions,
    inputNode
  );
  if (filterFn.nodeType !== 'var') {
    node = Op.opFilter({
      arr: node as any,
      filterFn: Op.constNodeUnsafe(filterFn.type, filterFn) as any,
    });
  }

  if (sortByColumns.length > 0) {
    const comparableDataFn = getComparableDataFn(
      columnSelectFunctions,
      sortByColumns,
      inputNode
    );
    node = Op.opSort({
      arr: node as any,
      comparableDataFn: Op.constNodeUnsafe(
        comparableDataFn.type,
        comparableDataFn
      ) as any,
      columnDirs: Op.constNodeUnsafe(
        {type: 'array', objectType: 'string'},
        sortByColumns.map(sort => sort.dir)
      ),
    });
  }

  return node;
}

export function getCellValueNode(
  rowNode: CG.Node,
  selectFunction: CG.NodeOrVoidNode
) {
  return selectFunction.nodeType !== 'void'
    ? HL.callFunction(selectFunction, {x: rowNode})
    : selectFunction;
}
